import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AuthService, ToastService } from '../../../../../libs/core';
import { LoginApiService, RefreshTokenResponse, Verify2faResponse } from './api-service/login-api-service/login-api.service';

interface JwtAuthSnapshot {
  userId: number | null;
  roleName: string;
  permissions: string[];
  email: string;
}

interface SessionAuthSeed {
  email?: string;
  company?: string;
  user?: Verify2faResponse['data']['user'] | RefreshTokenResponse['data']['user'];
}

interface SuperadminTokenSession {
  accessToken: string;
  accessTokenExpiresAtEpochMs: number;
  refreshToken: string;
  refreshTokenExpiresAtEpochMs: number;
  lastActivityAtEpochMs: number;
  persistLogin: boolean;
}

@Injectable({ providedIn: 'root' })
export class SuperadminAuthService {
  private readonly accessTokenCookie = 'wm_access_token';
  private readonly refreshTokenCookie = 'wm_refresh_token';
  private readonly lastProtectedRouteKey = 'superadmin.last-protected-route';
  private readonly sessionStorageKey = 'superadmin.auth-session';
  private readonly localStorageKey = 'superadmin.auth-session:persistent';
  private readonly defaultProtectedRoute = '/dashboard';
  private readonly refreshLeadTimeMs = 30000;
  private readonly idleTimeoutMs = 30 * 60 * 1000;
  private readonly inactivityToastMessage = 'You have been logged out due to inactivity.';
  private readonly sessionExpiredToastMessage = 'Your session has expired. Please login again.';
  private readonly activityPersistIntervalMs = 15000;
  private readonly idleLogIntervalMs = 5000;
  private readonly activityEvents: readonly (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly loginApiService = inject(LoginApiService);
  private session: SuperadminTokenSession | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private idleLogTimer: ReturnType<typeof setInterval> | null = null;
  private refreshRequest$: Observable<string> | null = null;
  private logoutRequestInFlight = false;
  private activityMonitoringInitialized = false;

  private readonly handleActivity = () => {
    this.recordActivity();
  };

  private readonly handleVisibilityChange = () => {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.hidden) {
      this.scheduleIdleTimeout();
      return;
    }

    this.checkIdleExpiry();
    this.recordActivity();
  };

  constructor() {
    this.initializeActivityMonitoring();
    this.restoreSession();
  }

  getAccessToken(): string {
    return this.session?.accessToken ?? this.getCookieValue(this.accessTokenCookie);
  }

  getRefreshToken(): string {
    return this.session?.refreshToken ?? this.getCookieValue(this.refreshTokenCookie);
  }

  isAuthenticated(): boolean {
    const session = this.getSession();
    if (!session) {
      return false;
    }

    const now = Date.now();
    return session.accessTokenExpiresAtEpochMs > now || session.refreshTokenExpiresAtEpochMs > now;
  }

  clearTokens(): void {
    this.stopRefreshTimer();
    this.stopIdleTimer();
    this.session = null;
    this.deleteCookie(this.accessTokenCookie);
    this.deleteCookie(this.refreshTokenCookie);
    this.removeStoredSession();
  }

  startSession(
    data: Verify2faResponse['data'] | RefreshTokenResponse['data'],
    persistLogin: boolean,
    lastActivityAtEpochMs = Date.now(),
    authSeed?: SessionAuthSeed
  ): void {
    const now = Date.now();
    const nextSession: SuperadminTokenSession = {
      accessToken: data.accessToken,
      accessTokenExpiresAtEpochMs: now + (data.accessTokenExpiresIn * 1000),    
      refreshToken: data.refreshToken,
      refreshTokenExpiresAtEpochMs: now + (data.refreshTokenExpiresIn * 1000),  
      lastActivityAtEpochMs,
      persistLogin
    };

    this.session = nextSession;
    this.persistSession(nextSession);
    this.writeTokenCookies(nextSession);
    this.syncCoreAuthSession(nextSession.accessToken, authSeed);
    this.scheduleRefresh();
    this.scheduleIdleTimeout();
  }

  ensureValidAccessToken(): Observable<string> {
    const session = this.getSession();
    if (!session) {
      return of('');
    }

    const now = Date.now();
    if (now - session.lastActivityAtEpochMs >= this.idleTimeoutMs) {
      this.handleExpiredSession(true, this.inactivityToastMessage);
      return of('');
    }

    if (session.accessTokenExpiresAtEpochMs > now + 5000) {
      return of(session.accessToken);
    }

    if (session.refreshTokenExpiresAtEpochMs <= now) {
      this.logout(true, this.sessionExpiredToastMessage);
      return of('');
    }

    return this.refreshAccessToken();
  }

  setLastProtectedRoute(url: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.isProtectedRoute(url)) {
      return;
    }

    localStorage.setItem(this.lastProtectedRouteKey, url);
  }

  getLastProtectedRoute(): string {
    if (typeof window === 'undefined') {
      return this.defaultProtectedRoute;
    }

    const saved = localStorage.getItem(this.lastProtectedRouteKey);
    if (!saved || !this.isProtectedRoute(saved)) {
      return this.defaultProtectedRoute;
    }

    return saved;
  }

  clearLastProtectedRoute(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(this.lastProtectedRouteKey);
  }

  clearSession(): void {
    this.clearTokens();
    this.clearLastProtectedRoute();
    this.authService.logout();
  }

  logout(redirectToLogin = true, toastMessage?: string): void {
    if (this.logoutRequestInFlight) {
      return;
    }

    this.logoutRequestInFlight = true;

    this.loginApiService.logout().pipe(
      catchError(() => of(null)),
      finalize(() => {
        this.logoutRequestInFlight = false;
        this.handleExpiredSession(redirectToLogin, toastMessage);
      })
    ).subscribe();
  }

  refreshAfterUnauthorized(): Observable<string> {
    console.warn('[Auth] Calling refresh token API after 401 response.');

    const session = this.getSession();
    if (!session?.refreshToken || session.refreshTokenExpiresAtEpochMs <= Date.now()) {
      this.logout(true, this.sessionExpiredToastMessage);
      return of('');
    }

    return this.refreshAccessToken();
  }

  private refreshAccessToken(): Observable<string> {
    const session = this.getSession();
    if (!session?.refreshToken) {
      this.logout(true, this.sessionExpiredToastMessage);
      return of('');
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshRequest$ = this.loginApiService.refreshToken({ refreshToken: session.refreshToken }).pipe(
      map((response) => {
        if (!response.success || !response.data?.accessToken || !response.data?.refreshToken) {
          this.handleRefreshTokenUnauthorized();
          return '';
        }

        this.startSession(response.data, session.persistLogin, session.lastActivityAtEpochMs, {
          user: response.data.user,
        });
        return response.data.accessToken;
      }),
      catchError((error: unknown) => {
        const httpError = error as HttpErrorResponse;
        if (httpError instanceof HttpErrorResponse && httpError.status === 401) {
          this.handleRefreshTokenUnauthorized();
          return of('');
        }

        this.handleRefreshTokenUnauthorized();
        return of('');
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  private getCookieValue(name: string): string {
    if (typeof document === 'undefined') {
      return '';
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  }

  private writeTokenCookies(session: SuperadminTokenSession): void {
    if (typeof document === 'undefined') {
      return;
    }

    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    const accessExpiresAttribute = session.persistLogin
      ? `; Expires=${new Date(session.accessTokenExpiresAtEpochMs).toUTCString()}`
      : '';
    const refreshExpiresAttribute = session.persistLogin
      ? `; Expires=${new Date(session.refreshTokenExpiresAtEpochMs).toUTCString()}`
      : '';

    document.cookie = `${this.accessTokenCookie}=${encodeURIComponent(session.accessToken)}${accessExpiresAttribute}; Path=/; SameSite=Lax${secure}`;
    document.cookie = `${this.refreshTokenCookie}=${encodeURIComponent(session.refreshToken)}${refreshExpiresAttribute}; Path=/; SameSite=Lax${secure}`;        
  }

  private restoreSession(): void {
    const stored = this.readStoredSession();
    if (!stored) {
      return;
    }

    this.session = stored;

    if (Date.now() - stored.lastActivityAtEpochMs >= this.idleTimeoutMs) {      
      this.handleExpiredSession(false);
      return;
    }

    if (stored.refreshTokenExpiresAtEpochMs <= Date.now()) {
      this.handleExpiredSession(false);
      return;
    }

    this.writeTokenCookies(stored);

    if (stored.accessTokenExpiresAtEpochMs <= Date.now()) {
      this.refreshAccessToken().subscribe();
      return;
    }

    this.scheduleRefresh();
    this.scheduleIdleTimeout();
  }

  private getSession(): SuperadminTokenSession | null {
    if (this.session) {
      return this.session;
    }

    const stored = this.readStoredSession();
    if (!stored) {
      return null;
    }

    this.session = stored;
    return stored;
  }

  private scheduleRefresh(): void {
    const session = this.getSession();
    if (!session) {
      return;
    }

    this.stopRefreshTimer();

    const delay = Math.max(session.accessTokenExpiresAtEpochMs - Date.now() - this.refreshLeadTimeMs, 0);
    this.refreshTimer = setTimeout(() => {
      this.refreshAccessToken().subscribe();
    }, delay);
  }

  private scheduleIdleTimeout(): void {
    const session = this.getSession();
    if (!session) {
      this.stopIdleTimer();
      this.stopIdleLogTimer();
      return;
    }

    this.stopIdleTimer();
    this.startIdleLogTimer();

    const remaining = Math.max(this.idleTimeoutMs - (Date.now() - session.lastActivityAtEpochMs), 0);
    this.idleTimer = setTimeout(() => {
      this.checkIdleExpiry();
    }, remaining);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private stopIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private startIdleLogTimer(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.stopIdleLogTimer();

    this.logIdleTimeRemaining();
    this.idleLogTimer = setInterval(() => {
      this.logIdleTimeRemaining();
    }, this.idleLogIntervalMs);
  }

  private stopIdleLogTimer(): void {
    if (this.idleLogTimer) {
      clearInterval(this.idleLogTimer);
      this.idleLogTimer = null;
    }
  }

  private persistSession(session: SuperadminTokenSession): void {
    if (typeof window === 'undefined') {
      return;
    }

    const serialized = JSON.stringify(session);
    if (session.persistLogin) {
      localStorage.setItem(this.localStorageKey, serialized);
      sessionStorage.removeItem(this.sessionStorageKey);
      return;
    }

    sessionStorage.setItem(this.sessionStorageKey, serialized);
    localStorage.removeItem(this.localStorageKey);
  }

  private readStoredSession(): SuperadminTokenSession | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const sessionRaw = sessionStorage.getItem(this.sessionStorageKey);
    const localRaw = localStorage.getItem(this.localStorageKey);
    const raw = sessionRaw || localRaw;

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<SuperadminTokenSession>;        
      if (
        !parsed.accessToken ||
        !parsed.refreshToken ||
        typeof parsed.accessTokenExpiresAtEpochMs !== 'number' ||
        typeof parsed.refreshTokenExpiresAtEpochMs !== 'number'
      ) {
        this.removeStoredSession();
        return null;
      }

      return {
        accessToken: parsed.accessToken,
        accessTokenExpiresAtEpochMs: parsed.accessTokenExpiresAtEpochMs,        
        refreshToken: parsed.refreshToken,
        refreshTokenExpiresAtEpochMs: parsed.refreshTokenExpiresAtEpochMs,      
        lastActivityAtEpochMs: typeof parsed.lastActivityAtEpochMs === 'number' ? parsed.lastActivityAtEpochMs : Date.now(),
        persistLogin: Boolean(parsed.persistLogin)
      };
    } catch {
      this.removeStoredSession();
      return null;
    }
  }

  private removeStoredSession(): void {
    if (typeof window === 'undefined') {
      return;
    }

    sessionStorage.removeItem(this.sessionStorageKey);
    localStorage.removeItem(this.localStorageKey);
  }

  private handleExpiredSession(redirectToLogin = true, toastMessage?: string): void {
    this.clearSession();

    if (!redirectToLogin || typeof window === 'undefined') {
      return;
    }

    const currentPath = this.router.url.split('?')[0];
    if (currentPath !== '/login') {
      if (toastMessage) {
        this.toastService.show(toastMessage, 'error');
      }
      void this.router.navigateByUrl('/login', { replaceUrl: true });
    }
  }

  private handleRefreshTokenUnauthorized(): void {
    this.clearSession();

    if (typeof window === 'undefined') {
      return;
    }

    const currentPath = this.router.url.split('?')[0];
    if (currentPath !== '/error/401') {
      this.toastService.show(this.sessionExpiredToastMessage, 'error');
      void this.router.navigateByUrl('/error/401', { replaceUrl: true });
    }
  }

  private isProtectedRoute(url: string): boolean {
    const clean = url.split('?')[0].split('#')[0];

    return (
      clean === '/dashboard' ||
      clean.startsWith('/dashboard/') ||
      clean === '/tenant' ||
      clean.startsWith('/tenant/') ||
      clean === '/user-management' ||
      clean.startsWith('/user-management/') ||
      clean === '/product-bank' ||
      clean.startsWith('/product-bank/') ||
      clean === '/support' ||
      clean.startsWith('/support/') ||
      clean === '/notification' ||
      clean.startsWith('/notification/') ||
      clean === '/profile' ||
      clean.startsWith('/profile/')
    );
  }

  private initializeActivityMonitoring(): void {
    if (this.activityMonitoringInitialized || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.activityMonitoringInitialized = true;

    for (const eventName of this.activityEvents) {
      window.addEventListener(eventName, this.handleActivity, { passive: true });
    }

    window.addEventListener('blur', this.handleVisibilityChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange); 
  }

  private recordActivity(forcePersist = false): void {
    const session = this.getSession();
    if (!session) {
      return;
    }

    const now = Date.now();
    if (now - session.lastActivityAtEpochMs >= this.idleTimeoutMs) {
      this.handleExpiredSession(true, this.inactivityToastMessage);
      return;
    }

    const nextSession: SuperadminTokenSession = {
      ...session,
      lastActivityAtEpochMs: now
    };

    this.session = nextSession;

    if (forcePersist || now - session.lastActivityAtEpochMs >= this.activityPersistIntervalMs) {
      this.persistSession(nextSession);
    }

    this.scheduleIdleTimeout();
  }

  private checkIdleExpiry(): void {
    const session = this.getSession();
    if (!session) {
      this.stopIdleLogTimer();
      return;
    }

    if (Date.now() - session.lastActivityAtEpochMs >= this.idleTimeoutMs) {
      this.handleExpiredSession(true, this.inactivityToastMessage);
      return;
    }

    this.scheduleIdleTimeout();
  }

  private logIdleTimeRemaining(): void {
    if (typeof console === 'undefined') {
      return;
    }

    const session = this.getSession();
    if (!session) {
      this.stopIdleLogTimer();
      return;
    }

    const remainingMs = Math.max(this.idleTimeoutMs - (Date.now() - session.lastActivityAtEpochMs), 0);


    if (remainingMs === 0) {
      this.stopIdleLogTimer();
    }
  }

  private syncCoreAuthSession(accessToken: string, authSeed?: SessionAuthSeed): void {
    const tokenSnapshot = this.extractAuthSnapshotFromJwt(accessToken);
    const currentSession = this.authService.session();
    const userFromApi = authSeed?.user;

    const email = authSeed?.email ?? tokenSnapshot.email ?? currentSession.email;
    const company = authSeed?.company ?? currentSession.company ?? 'WealthMax Superadmin';
    const roleName = userFromApi?.roleName ?? tokenSnapshot.roleName ?? currentSession.roleName;
    const permissions = userFromApi?.permissions ?? tokenSnapshot.permissions ?? currentSession.permissions;
    const userId = userFromApi?.id ?? tokenSnapshot.userId ?? currentSession.userId;
    const roleId = userFromApi?.roleId ?? currentSession.roleId;
    const firstName = userFromApi?.firstName ?? currentSession.firstName;
    const lastName = userFromApi?.lastName ?? currentSession.lastName;

    this.authService.login(
      {
        company,
        email,
      },
      'superadmin',
      permissions,
      {
        userId,
        roleId,
        roleName,
        firstName,
        lastName,
      }
    );
  }

  private extractAuthSnapshotFromJwt(accessToken: string): JwtAuthSnapshot {
    const fallback: JwtAuthSnapshot = {
      userId: null,
      roleName: '',
      permissions: [],
      email: '',
    };

    if (!accessToken) {
      return fallback;
    }

    const parts = accessToken.split('.');
    if (parts.length < 2 || !parts[1]) {
      return fallback;
    }

    try {
      const payloadJson = this.decodeBase64Url(parts[1]);
      const payload = JSON.parse(payloadJson) as Record<string, unknown>;

      const roleClaim = payload['role'] ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const permissionClaim = payload['permissions'] ?? payload['permission'];
      const emailClaim = payload['email'] ?? payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'];
      const userIdClaim = payload['sub'] ?? payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];

      return {
        userId: this.toNumberOrNull(userIdClaim),
        roleName: this.toSingleString(roleClaim),
        permissions: this.toStringArray(permissionClaim),
        email: this.toSingleString(emailClaim),
      };
    } catch {
      return fallback;
    }
  }

  private decodeBase64Url(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return atob(padded);
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }

    if (typeof value === 'string') {
      return [value];
    }

    return [];
  }

  private toSingleString(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }

    return '';
  }

  private toNumberOrNull(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }
}